import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Award,
  BookOpen,
  Briefcase,
  Calendar,
  Code,
  FolderTree,
  GraduationCap,
  KeyRound,
  Mail,
  Menu,
  MessageSquare,
  Settings,
  Sparkles,
  BarChart3,
  ChevronDown,
  FileText,
  LifeBuoy,
  UserCog,
  Users,
  X,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { contentApi } from '../../services/contentApi';
import { interviewApi, interviewerApi, mentorApi } from '../../services/api';
import { API_PATHS, apiHeaders, apiUrl } from '../../config/api';
import { ContentDashboard } from '../../types/content';
import {
  canAccessAdmin,
  canManageInterviews,
  canManageUsers,
  canReview,
  canReviewGigs,
  canReviewProjects,
  canWriteCourses,
  canWriteEvents,
  canWriteGigs,
  canWriteLabs,
  canWriteProjects,
  isAcademyAdmin,
  isChallengeAdmin,
  isCourseOwner,
  isInterviewAdmin,
  isOwner,
} from '../../utils/roles';
import Button from '../../components/UI/Button';
import ActionStatus from '../../components/UI/ActionStatus';
import { useAutoClear } from '../../hooks/useAutoClear';
import { notifyPublicNavChanged } from '../../hooks/usePublicNav';
import Card from '../../components/UI/Card';
import ReviewQueuePage from './ReviewQueuePage';
import CategoriesPage from './CategoriesPage';
import AuthorsPage from './AuthorsPage';
import LlmConfigPage from './LlmConfigPage';
import CatalogSettingsPage from './CatalogSettingsPage';
import LegalDocumentsAdminPage from './LegalDocumentsAdminPage';
import CatalogTreePanel from '../../components/content/CatalogTreePanel';
import MyProjectsPage from '../content/MyProjectsPage';
import MyGigsPage from '../content/MyGigsPage';
import MyEventsPage from '../content/MyEventsPage';
import UsersAdminPage from './UsersAdminPage';
import ReportsAdminPage from './ReportsAdminPage';
import AcademyReportsAdminPage from './AcademyReportsAdminPage';
import ProjectReportsAdminPage from './ProjectReportsAdminPage';
import ProjectMentorRequestsAdminPage from './ProjectMentorRequestsAdminPage';
import InterviewReportsPanel from './InterviewReportsPanel';
import LearnersAdminPage from './LearnersAdminPage';
import SchoolAdminPanel from './SchoolAdminPanel';
import ContributorApplicationsPanel from './ContributorApplicationsPanel';
import CourseOwnerPanel from './CourseOwnerPanel';
import { focusPageTop } from '../../utils/focusPageTop';

type AdminTab =
  | 'overview'
  | 'courses'
  | 'labs'
  | 'projects'
  | 'gigs'
  | 'challenges'
  | 'earnings'
  | 'events'
  | 'menus'
  | 'review'
  | 'categories'
  | 'authors'
  | 'llm'
  | 'users'
  | 'reports'
  | 'academy-reports'
  | 'project-reports'
  | 'project-mentor-requests'
  | 'interview-reports'
  | 'learners'
  | 'school-programs'
  | 'school-interviews'
  | 'school-inquiries'
  | 'school-emails'
  | 'mentor-applications'
  | 'interviewer-applications'
  | 'catalog-settings'
  | 'legal';

const TAB_TITLES: Record<AdminTab, string> = {
  overview: 'Admin Dashboard',
  courses: 'Courses',
  labs: 'Labs',
  projects: 'Projects',
  gigs: 'Gigs',
  challenges: 'Challenges',
  earnings: 'My courses & revenue',
  events: 'Events',
  menus: 'Menus',
  review: 'Review Queue',
  categories: 'Categories',
  authors: 'Authors',
  llm: 'LLM Configuration',
  users: 'Users',
  reports: 'Course reports',
  'academy-reports': 'Academy reports',
  'project-reports': 'Project reports',
  'project-mentor-requests': 'Project mentor requests',
  'interview-reports': 'Interview reports',
  learners: 'Onboarding',
  'school-programs': 'IDC Academy',
  'school-interviews': 'Interviews',
  'school-inquiries': 'Inquiries',
  'school-emails': 'Email Log',
  'mentor-applications': 'Mentor applications',
  'interviewer-applications': 'Interviewer applications',
  'catalog-settings': 'General settings',
  legal: 'Terms & Privacy',
};

type SidebarItem = {
  key: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  show: boolean;
  href?: string;
};

type SidebarGroup = {
  id: string;
  label?: string;
  collapsible?: boolean;
  items: SidebarItem[];
};

type OpenRequestGroupId = 'user' | 'contributor';

type OpenRequestCard = {
  title: string;
  value: number | null;
  detail: string;
  tab: AdminTab;
  show: boolean;
  icon: React.ComponentType<{ className?: string }>;
  group: OpenRequestGroupId;
};

const OPEN_REQUEST_GROUPS: { id: OpenRequestGroupId; label: string; description: string }[] = [
  {
    id: 'user',
    label: 'User-initiated',
    description: 'Requests from learners and the public.',
  },
  {
    id: 'contributor',
    label: 'Admin & contributor initiated',
    description: 'Content submitted for review, and people applying to contribute.',
  },
];

const isPendingStatus = (status?: string | null) => {
  const value = (status || '').toLowerCase();
  return !value || value === 'pending' || value === 'pending review';
};

const ContentAdminDashboard: React.FC = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = (() => {
    const raw = searchParams.get('tab');
    if (raw === 'idschool') return 'school-programs' as AdminTab;
    if (raw && raw in TAB_TITLES) return raw as AdminTab;
    return 'overview';
  })();
  const [activeTab, setActiveTab] = useState<AdminTab>(requestedTab);
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => window.innerWidth >= 1024);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [data, setData] = useState<ContentDashboard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingInterviews, setPendingInterviews] = useState<number | null>(null);
  const [pendingInterviewers, setPendingInterviewers] = useState<number | null>(null);
  const [pendingMentors, setPendingMentors] = useState<number | null>(null);
  const [pendingMentorRequests, setPendingMentorRequests] = useState<number | null>(null);
  const [pendingEnquiries, setPendingEnquiries] = useState<number | null>(null);
  const [academyProgramCount, setAcademyProgramCount] = useState<number | null>(null);
  const [pendingAdmissions, setPendingAdmissions] = useState<number | null>(null);

  useEffect(() => {
    setActiveTab(requestedTab);
  }, [requestedTab]);

  useEffect(() => {
    const handleResize = () => setIsSidebarOpen(window.innerWidth >= 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (window.innerWidth < 1024 && isSidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isSidebarOpen]);

  useEffect(() => {
      contentApi.dashboard()
      .then((r) => setData(r.data.dashboard))
      .catch(() => setData(null));

    if (isOwner(user) || isInterviewAdmin(user)) {
      interviewApi.getReports()
        .then((r) => setPendingInterviews(r.data.reports?.pendingCount ?? 0))
        .catch(() => undefined);
      interviewerApi.applications()
        .then((r) => {
          const list = r.data.interviewers || [];
          setPendingInterviewers(list.filter((row) => isPendingStatus(row.approvalStatus)).length);
        })
        .catch(() => undefined);
    }

    if (canReview(user)) {
      contentApi.listProjectMentorRequests({ status: 'Open' })
        .then((r) => setPendingMentorRequests((r.data.requests || []).length))
        .catch(() => undefined);
    }

    if (isOwner(user) || isAcademyAdmin(user)) {
      mentorApi.list()
        .then((r) => {
          const list = (r.data.mentors || []) as Array<{ approvalStatus?: string }>;
          setPendingMentors(list.filter((row) => isPendingStatus(row.approvalStatus)).length);
        })
        .catch(() => undefined);

      fetch(`${apiUrl(API_PATHS.courses.list)}?enrollmentAllowedOnly=false`, { headers: apiHeaders(token) })
        .then((res) => res.json())
        .then((body) => setAcademyProgramCount((body?.courses || []).length))
        .catch(() => undefined);

      fetch(apiUrl(API_PATHS.courses.enrollmentCounts), { headers: apiHeaders(token) })
        .then((res) => res.json())
        .then((body) => {
          const rows = body?.courseEnrollmentCounts || [];
          const total = rows.reduce((sum: number, item: { pendingEnrollmentCount?: number }) => sum + (item.pendingEnrollmentCount || 0), 0);
          setPendingAdmissions(total);
        })
        .catch(() => undefined);

      fetch(`${apiUrl(API_PATHS.inquiries.list)}?pageNumber=1&pageSize=1`, { headers: apiHeaders(token) })
        .then((res) => res.json())
        .then((body) => {
          if (typeof body?.totalCount === 'number') setPendingEnquiries(body.totalCount);
          else if (typeof body?.total === 'number') setPendingEnquiries(body.total);
          else setPendingEnquiries((body?.inquiries || []).length);
        })
        .catch(() => undefined);
    }
  }, [user, token]);

  const selectTab = (tab: AdminTab) => {
    setActiveTab(tab);
    setSearchParams(tab === 'overview' ? {} : { tab }, { replace: true });
    if (window.innerWidth < 1024) setIsSidebarOpen(false);
    focusPageTop();
  };

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const didInitSidebar = useRef(false);

  const sidebarGroups: SidebarGroup[] = useMemo(() => [
    {
      id: 'overview',
      label: 'General',
      collapsible: true,
      items: [
        { key: 'overview', label: 'Overview', icon: Award, show: canAccessAdmin(user) },
        { key: 'catalog-settings', label: 'General settings', icon: Settings, show: isOwner(user) },
        { key: 'legal', label: 'Terms & Privacy', icon: FileText, show: isOwner(user) },
        { key: 'school-inquiries', label: 'Inquiries', icon: Mail, show: isAcademyAdmin(user) || isOwner(user) },
        { key: 'users', label: 'Users', icon: UserCog, show: canManageUsers(user) },
        { key: 'menus', label: 'Menus', icon: Menu, show: isOwner(user) },
        { key: 'categories', label: 'Categories', icon: FolderTree, show: isOwner(user) },
      ],
    },
    {
      id: 'academy',
      label: 'IDC Academy',
      collapsible: true,
      items: [
        { key: 'school-programs', label: 'Programs', icon: GraduationCap, show: isAcademyAdmin(user) },
        { key: 'mentor-applications', label: 'Mentors', icon: GraduationCap, show: isAcademyAdmin(user) },
        { key: 'academy-reports', label: 'Academy reports', icon: BarChart3, show: isAcademyAdmin(user) },
        { key: 'school-emails', label: 'Email', icon: Mail, show: isAcademyAdmin(user) },
      ],
    },
    {
      id: 'interviews',
      label: 'Interviews',
      collapsible: true,
      items: [
        { key: 'school-interviews', label: 'Interviews', icon: MessageSquare, show: canManageInterviews(user) },
        { key: 'interviewer-applications', label: 'Interviewers', icon: Users, show: isInterviewAdmin(user) },
        { key: 'interview-reports', label: 'Interview reports', icon: BarChart3, show: isInterviewAdmin(user) },
      ],
    },
    {
      id: 'courses',
      label: 'Courses',
      collapsible: true,
      items: [
        { key: 'courses', label: 'Courses', icon: BookOpen, show: canWriteCourses(user) },
        { key: 'labs', label: 'Labs', icon: Code, show: canWriteLabs(user) },
        { key: 'generate', label: 'AI generator', icon: Sparkles, show: canWriteLabs(user), href: '/admin/generate' },
        { key: 'projects', label: 'Projects', icon: Briefcase, show: canWriteProjects(user) },
        { key: 'project-reports', label: 'Project reports', icon: BarChart3, show: canReview(user) },
        { key: 'project-mentor-requests', label: 'Project mentor requests', icon: LifeBuoy, show: canReview(user) },
        { key: 'reports', label: 'Course reports', icon: BarChart3, show: canReview(user) },
        { key: 'review', label: 'Review', icon: Sparkles, show: canReview(user) || canReviewGigs(user) },
        { key: 'learners', label: 'Invitations', icon: KeyRound, show: canWriteCourses(user) },
        { key: 'earnings', label: 'My courses', icon: BarChart3, show: isCourseOwner(user) },
      ],
    },
    {
      id: 'other',
      label: 'Other',
      collapsible: true,
      items: [
        { key: 'events', label: 'Events', icon: Calendar, show: canWriteEvents(user) },
        { key: 'gigs', label: 'Gigs', icon: Briefcase, show: canWriteGigs(user) },
        { key: 'challenges', label: 'Challenges', icon: Sparkles, show: isChallengeAdmin(user) },
        { key: 'authors', label: 'Authors', icon: Users, show: isOwner(user) },
        { key: 'llm', label: 'LLM', icon: Settings, show: isOwner(user) },
      ],
    },
  ], [user]);

  useEffect(() => {
    const groupForTab = sidebarGroups.find((g) => g.items.some((item) => item.show && item.key === activeTab));
    const firstOther = sidebarGroups.find((g) => g.id !== 'overview' && g.items.some((item) => item.show));
    if (!didInitSidebar.current) {
      didInitSidebar.current = true;
      if (groupForTab && groupForTab.id !== 'overview') {
        setExpandedGroups({ [groupForTab.id]: true });
      } else if (firstOther) {
        setExpandedGroups({ [firstOther.id]: true });
      } else if (groupForTab) {
        setExpandedGroups({ [groupForTab.id]: true });
      }
      return;
    }
    if (groupForTab?.collapsible) {
      setExpandedGroups((prev) => (prev[groupForTab.id] && Object.keys(prev).length === 1 ? prev : { [groupForTab.id]: true }));
    }
  }, [activeTab, sidebarGroups]);

  useEffect(() => {
    const nav = sidebarRef.current?.querySelector<HTMLButtonElement>(`button[data-admin-nav="${CSS.escape(activeTab)}"]`);
    if (!nav) return;
    if (window.innerWidth < 1024 && !isSidebarOpen) return;
    nav.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    nav.focus({ preventScroll: true });
  }, [activeTab, expandedGroups, isSidebarOpen]);

  const courseReviewCount = data ? data.coursesPendingReview : null;
  const labReviewCount = data ? data.labsPendingReview : null;
  const projectReviewCount = data ? data.projectsPendingReview : null;
  const gigReviewCount = data ? (data.gigsPendingReview ?? 0) : null;
  const eventReviewCount = data ? (data.eventsPendingReview ?? 0) : null;
  const reviewQueueCount = [courseReviewCount, labReviewCount, projectReviewCount, gigReviewCount]
    .reduce((sum: number, value) => sum + (value || 0), 0);

  const menuBadge = (tab: AdminTab): number | null => {
    const counts: Partial<Record<AdminTab, number | null>> = {
      'school-inquiries': pendingEnquiries,
      'mentor-applications': pendingMentors,
      'interviewer-applications': pendingInterviewers,
      'school-interviews': pendingInterviews,
      'interview-reports': pendingInterviews,
      'project-mentor-requests': pendingMentorRequests,
      'school-programs': pendingAdmissions,
      'academy-reports': pendingAdmissions,
      review: reviewQueueCount,
      courses: courseReviewCount,
      labs: labReviewCount,
      projects: projectReviewCount,
      gigs: gigReviewCount,
      events: eventReviewCount,
    };
    const value = counts[tab];
    return value != null && value > 0 ? value : null;
  };

  const openRequestCards: OpenRequestCard[] = [
    {
      title: 'Project mentor requests',
      value: pendingMentorRequests,
      detail: 'Learners asking for a project mentor',
      tab: 'project-mentor-requests',
      show: canReview(user) && pendingMentorRequests != null,
      icon: LifeBuoy,
      group: 'user',
    },
    {
      title: 'Interview requests',
      value: pendingInterviews,
      detail: 'Waiting to be scheduled',
      tab: 'school-interviews',
      show: (isOwner(user) || isInterviewAdmin(user)) && pendingInterviews != null,
      icon: MessageSquare,
      group: 'user',
    },
    {
      title: 'Inquiries',
      value: pendingEnquiries,
      detail: 'Contact / academy inquiries',
      tab: 'school-inquiries',
      show: isAcademyAdmin(user) && pendingEnquiries != null,
      icon: Mail,
      group: 'user',
    },
    {
      title: 'Course',
      value: courseReviewCount,
      detail: 'Awaiting review / publish',
      tab: 'review',
      show: canReview(user) && courseReviewCount != null,
      icon: BookOpen,
      group: 'contributor',
    },
    {
      title: 'Labs',
      value: labReviewCount,
      detail: 'Awaiting review / publish',
      tab: 'review',
      show: canReview(user) && labReviewCount != null,
      icon: Code,
      group: 'contributor',
    },
    {
      title: 'Projects',
      value: projectReviewCount,
      detail: 'Awaiting review / publish',
      tab: 'review',
      show: (canReview(user) || canReviewProjects(user)) && projectReviewCount != null,
      icon: Briefcase,
      group: 'contributor',
    },
    {
      title: 'Interviewers',
      value: pendingInterviewers,
      detail: 'Pending approval',
      tab: 'interviewer-applications',
      show: isInterviewAdmin(user) && pendingInterviewers != null,
      icon: Users,
      group: 'contributor',
    },
    {
      title: 'Mentors',
      value: pendingMentors,
      detail: 'Pending approval',
      tab: 'mentor-applications',
      show: isAcademyAdmin(user) && pendingMentors != null,
      icon: GraduationCap,
      group: 'contributor',
    },
  ].filter((card) => card.show);

  const academyOverviewCards = [
    {
      title: 'Programs',
      value: academyProgramCount,
      detail: pendingAdmissions != null ? `${pendingAdmissions} pending enrollments` : 'Academy programs',
      tab: 'school-programs' as AdminTab,
      show: isAcademyAdmin(user),
      icon: GraduationCap,
    },
    {
      title: 'Email',
      value: 'Log',
      detail: 'Academy message log',
      tab: 'school-emails' as AdminTab,
      show: isAcademyAdmin(user),
      icon: Mail,
    },
    {
      title: 'Academy reports',
      value: 'Open',
      detail: 'Academy enrollments only',
      tab: 'academy-reports' as AdminTab,
      show: isAcademyAdmin(user),
      icon: BarChart3,
    },
  ].filter((card) => card.show);

  const reportCards = [
    {
      title: 'Academy reports',
      value: 'Open',
      detail: 'Program enrollments',
      tab: 'academy-reports' as AdminTab,
      show: isAcademyAdmin(user),
      icon: GraduationCap,
    },
    {
      title: 'Interview reports',
      value: pendingInterviews,
      detail: 'Pending interview requests',
      tab: 'interview-reports' as AdminTab,
      show: isInterviewAdmin(user),
      icon: MessageSquare,
    },
    {
      title: 'Course reports',
      value: 'Open',
      detail: 'Course enrollments and fees',
      tab: 'reports' as AdminTab,
      show: canReview(user),
      icon: BookOpen,
    },
    {
      title: 'Project reports',
      value: 'Open',
      detail: 'Project enrollments',
      tab: 'project-reports' as AdminTab,
      show: canReview(user),
      icon: Briefcase,
    },
  ].filter((card) => card.show);

  const catalogCards = data ? [
    { title: 'Courses', value: data.coursesTotal, detail: `${data.coursesDraft} draft · ${data.coursesPendingReview} review · ${data.coursesPublished} live`, icon: BookOpen },
    { title: 'Labs', value: data.labsTotal, detail: `${data.labsDraft} draft · ${data.labsPendingReview} review · ${data.labsPublished} live`, icon: Code },
    { title: 'Projects', value: data.projectsTotal, detail: `${data.projectsDraft} draft · ${data.projectsPendingReview} review · ${data.projectsPublished} live`, icon: Briefcase },
    { title: 'Gigs', value: data.gigsTotal ?? 0, detail: `${data.gigsDraft ?? 0} draft · ${data.gigsPendingReview ?? 0} review · ${data.gigsPublished ?? 0} live`, icon: Briefcase },
    { title: 'Events', value: data.eventsTotal ?? 0, detail: `${data.eventsDraft ?? 0} draft · ${data.eventsPendingReview ?? 0} review · ${data.eventsPublished ?? 0} live`, icon: Calendar },
    { title: 'Authors', value: data.authorsTotal, detail: 'Content creators', icon: Users },
    { title: 'AI today', value: data.aiGeneratedToday, detail: `${data.aiSuccessToday} ok · ${data.aiFailureToday} failed`, icon: Sparkles },
  ] : [];

  const showCourseSubTabs = activeTab === 'courses' || activeTab === 'labs' || activeTab === 'projects' || activeTab === 'project-reports'
    || activeTab === 'project-mentor-requests'
    || activeTab === 'reports';
  const showInterviewSubTabs = activeTab === 'school-interviews' || activeTab === 'interviewer-applications' || activeTab === 'interview-reports';
  const showAcademySubTabs = activeTab === 'school-programs'
    || activeTab === 'academy-reports' || activeTab === 'school-emails' || activeTab === 'mentor-applications';

  const renderSubTabs = (
    tabs: { key: AdminTab; label: string; show: boolean }[],
  ) => {
    const visible = tabs.filter((tab) => tab.show);
    if (!visible.length) return null;
    return (
      <div className="mb-6 flex flex-wrap gap-2 border-b border-gray-200 pb-3">
        {visible.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => selectTab(tab.key)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? 'bg-primary-600 text-white'
                : 'bg-white text-gray-700 border border-gray-200 hover:bg-primary-50 hover:text-primary-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
    );
  };

  const renderNavCardGrid = (
    cards: { title: string; value?: number | string | null; detail: string; tab: AdminTab; icon: React.ComponentType<{ className?: string }> }[],
  ) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {cards.map((stat, index) => {
        const Icon = stat.icon;
        return (
          <motion.div key={stat.title} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: index * 0.06 }}>
            <button
              type="button"
              onClick={() => selectTab(stat.tab)}
              className="w-full text-left rounded-xl border border-primary-200 bg-primary-50/80 p-5 shadow-sm transition hover:border-primary-400 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-primary-600 mr-4">
                  <Icon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-primary-800 text-sm font-medium">{stat.title}</p>
                  <p className="text-2xl font-semibold text-primary-900">{stat.value ?? '—'}</p>
                  <p className="text-xs text-amber-800 font-medium mt-1">{stat.detail}</p>
                </div>
              </div>
            </button>
          </motion.div>
        );
      })}
    </div>
  );

  return (
    <div className="flex h-[calc(100dvh-4.5rem)] bg-transparent">
      {isSidebarOpen && window.innerWidth < 1024 && (
        <div
          className="fixed inset-0 z-20 bg-black bg-opacity-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
          aria-label="Close sidebar overlay"
        />
      )}
      <aside
        ref={sidebarRef}
        className={`bg-white/90 backdrop-blur-md shadow-md fixed inset-y-0 left-0 z-30 w-64 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="h-full flex flex-col">
          <div className="p-4 border-b flex items-center justify-between">
            <span className="text-xl font-bold text-primary-600">IDCoder Admin</span>
            <button type="button" className="lg:hidden p-2 rounded-md text-gray-500" onClick={() => setIsSidebarOpen(false)}>
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 overflow-y-auto py-4">
            <ul className="space-y-1 px-4">
              {sidebarGroups.map((group) => {
                const visible = group.items.filter((item) => item.show);
                if (!visible.length) return null;
                const isOpen = !group.collapsible || !!expandedGroups[group.id];
                const groupSelected = visible.some((item) => item.key === activeTab);
                return (
                  <li key={group.id} className="pt-2 first:pt-0">
                    {group.label && group.collapsible ? (
                      <button
                        type="button"
                        className={`flex w-full items-center justify-between rounded-md px-4 pb-1.5 pt-2 text-xs uppercase tracking-wide transition-colors ${
                          groupSelected
                            ? 'bg-primary-50 font-bold text-primary-800'
                            : 'font-semibold text-gray-500 hover:text-gray-700'
                        }`}
                        onClick={() => setExpandedGroups(isOpen ? {} : { [group.id]: true })}
                        aria-expanded={isOpen}
                      >
                        <span>{group.label}</span>
                        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isOpen ? '' : '-rotate-90'}`} />
                      </button>
                    ) : group.label ? (
                      <p className={`px-4 pb-1 pt-2 text-xs uppercase tracking-wide ${
                        groupSelected ? 'font-bold text-primary-800' : 'font-semibold text-gray-500'
                      }`}>
                        {group.label}
                      </p>
                    ) : null}
                    {isOpen && (
                      <ul className="space-y-1">
                        {visible.map((opt) => {
                          const Icon = opt.icon;
                          const selected = opt.href ? location.pathname === opt.href : activeTab === opt.key;
                          const badge = opt.key in TAB_TITLES ? menuBadge(opt.key as AdminTab) : null;
                          return (
                            <li key={`${group.id}-${opt.key}-${opt.label}`}>
                              <button
                                type="button"
                                data-admin-nav={opt.key}
                                aria-current={selected ? 'page' : undefined}
                                className={`flex items-center w-full px-4 py-2 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300 focus-visible:ring-offset-1 ${
                                  selected
                                    ? 'bg-primary-600 text-white font-semibold shadow-sm'
                                    : 'hover:bg-gray-100 text-gray-700'
                                }`}
                                onClick={() => {
                                  if (opt.href) {
                                    navigate(opt.href);
                                    if (window.innerWidth < 1024) setIsSidebarOpen(false);
                                    return;
                                  }
                                  selectTab(opt.key as AdminTab);
                                }}
                              >
                                <Icon className={`h-5 w-5 mr-3 shrink-0 ${selected ? 'text-white' : 'text-gray-500'}`} />
                                <span className="min-w-0 truncate text-left">{opt.label}</span>
                                {badge != null && (
                                  <span className={`ml-auto shrink-0 rounded-full px-1.5 py-0.5 text-[11px] font-bold tabular-nums ${
                                    selected ? 'bg-white/20 text-white' : 'bg-primary-100 text-primary-800'
                                  }`}>
                                    ({badge})
                                  </span>
                                )}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              })}
              <li className="mt-8">
                <button type="button" onClick={logout} className="w-full flex items-center px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md">
                  <X className="h-5 w-5 mr-3" />
                  Logout
                </button>
              </li>
            </ul>
          </nav>
          <div className="p-4 border-t">
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mr-3">
                <span className="font-semibold text-primary-700">
                  {user?.username?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'A'}
                </span>
              </div>
              <div>
                <p className="font-medium">{user?.username || user?.email?.split('@')[0]}</p>
                <p className="text-xs text-gray-500">{isOwner(user) ? 'Owner · Super admin' : user?.role}</p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden overflow-y-auto bg-transparent" data-page-scroll>
        <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="flex justify-between items-center mb-6 gap-4">
            <div className="min-w-0">
              {activeTab !== 'overview' && (
                <button
                  type="button"
                  className="mb-1 text-sm font-medium text-primary-700 hover:text-primary-800"
                  onClick={() => selectTab('overview')}
                >
                  ← Back to overview
                </button>
              )}
              <h1 className="text-2xl sm:text-3xl font-semibold text-gray-800 break-words">{TAB_TITLES[activeTab]}</h1>
            </div>
            <button type="button" className="lg:hidden p-2 rounded-md text-gray-700" onClick={() => setIsSidebarOpen(true)}>
              <Menu className="h-6 w-6" />
            </button>
          </div>

          {showCourseSubTabs && renderSubTabs([
            { key: 'courses', label: 'Courses', show: canWriteCourses(user) },
            { key: 'labs', label: 'Labs', show: canWriteLabs(user) },
            { key: 'projects', label: 'Projects', show: canWriteProjects(user) },
            { key: 'project-reports', label: 'Project reports', show: canReview(user) },
            { key: 'project-mentor-requests', label: 'Mentor requests', show: canReview(user) },
            { key: 'reports', label: 'Course reports', show: canReview(user) },
          ])}

          {showInterviewSubTabs && renderSubTabs([
            { key: 'interviewer-applications', label: 'Interviewers', show: isInterviewAdmin(user) },
            { key: 'school-interviews', label: 'Interviews', show: canManageInterviews(user) },
            { key: 'interview-reports', label: 'Interview reports', show: isInterviewAdmin(user) },
          ])}

          {showAcademySubTabs && renderSubTabs([
            { key: 'school-programs', label: 'Programs', show: isAcademyAdmin(user) },
            { key: 'mentor-applications', label: 'Mentors', show: isAcademyAdmin(user) },
            { key: 'academy-reports', label: 'Academy reports', show: isAcademyAdmin(user) },
            { key: 'school-emails', label: 'Email', show: isAcademyAdmin(user) },
          ])}

          {activeTab === 'overview' && (
            <div>
              {error && !data && <p className="text-gray-600 mb-4">{error}</p>}

              <div className="mb-8">
                <h2 className="text-lg font-semibold text-gray-800 mb-1">Actions</h2>
                <p className="text-xs text-gray-500 mb-4">Create catalog items or open the review queue.</p>
                <div className="flex flex-wrap gap-3">
                {canWriteCourses(user) && <Link to="/my-courses/new"><Button>New Course</Button></Link>}
                {canWriteLabs(user) && <Link to="/admin/labs/new"><Button variant="secondary">New Lab</Button></Link>}
                {canWriteProjects(user) && <Link to="/admin/projects/new"><Button>New Project</Button></Link>}
                {canWriteGigs(user) && <Link to="/admin/gigs/new"><Button variant="secondary">New Gig</Button></Link>}
                {canWriteEvents(user) && <Link to="/admin/events/new"><Button>New Event</Button></Link>}
                {(canReview(user) || canReviewProjects(user)) && (
                  <Button variant="outline" type="button" onClick={() => selectTab('review')}>Review Queue</Button>
                )}
                </div>
              </div>

              {academyOverviewCards.length > 0 && (
                <div className="mb-8">
                  <h2 className="text-lg font-semibold text-gray-800 mb-1">IDC Academy</h2>
                  <p className="text-xs text-gray-500 mb-4">Programs, email, and academy enrollment reports.</p>
                  {renderNavCardGrid(academyOverviewCards)}
                </div>
              )}

              {openRequestCards.length > 0 && (
                <div className="mb-8 space-y-6">
                  <h2 className="text-lg font-semibold text-gray-800">Open requests</h2>
                  {OPEN_REQUEST_GROUPS.map((group) => {
                    const cards = openRequestCards.filter((card) => card.group === group.id);
                    if (!cards.length) return null;
                    return (
                      <div key={group.id}>
                        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-600">{group.label}</h3>
                        <p className="text-xs text-gray-500 mt-1 mb-3">{group.description}</p>
                        {renderNavCardGrid(cards)}
                      </div>
                    );
                  })}
                </div>
              )}

              {reportCards.length > 0 && (
                <div className="mb-8">
                  <h2 className="text-lg font-semibold text-gray-800 mb-1">Reports</h2>
                  <p className="text-xs text-gray-500 mb-4">Open academy, interview, course, and project reports.</p>
                  {renderNavCardGrid(reportCards)}
                </div>
              )}

              {catalogCards.length > 0 && (
                <div className="mb-8">
                  <h2 className="text-lg font-semibold text-gray-800 mb-4">Catalog</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {catalogCards.map((stat, index) => {
                      const Icon = stat.icon;
                      return (
                        <motion.div key={stat.title} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: index * 0.08 }}>
                          <Card className="p-6">
                            <div className="flex items-center">
                              <div className="p-3 rounded-full bg-primary-100 mr-4">
                                <Icon className="h-6 w-6 text-primary-600" />
                              </div>
                              <div>
                                <p className="text-gray-500 text-sm">{stat.title}</p>
                                <p className="text-2xl font-semibold text-gray-800">{stat.value}</p>
                                <p className="text-xs text-amber-800 font-medium mt-1">{stat.detail}</p>
                              </div>
                            </div>
                          </Card>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'courses' && (
            <div className="space-y-8">
              <div className="flex justify-end">
                <Link to="/my-courses/new"><Button>New course</Button></Link>
              </div>
              <CatalogTreePanel kind="courses" />
            </div>
          )}

          {activeTab === 'labs' && (
            <div className="space-y-8">
              <div className="flex justify-end">
                <Link to="/admin/labs/new"><Button>New lab</Button></Link>
              </div>
              <CatalogTreePanel kind="labs" />
            </div>
          )}

          {activeTab === 'projects' && (
            <div className="[&>div]:max-w-none [&>div]:px-0 [&>div]:py-0 [&>div>div:first-child>h1]:sr-only">
              <MyProjectsPage />
            </div>
          )}

          {activeTab === 'gigs' && (
            <div className="[&>div]:max-w-none [&>div]:px-0 [&>div]:py-0 [&>div>div:first-child>h1]:sr-only">
              <MyGigsPage />
            </div>
          )}

          {activeTab === 'events' && (
            <div className="[&>div]:max-w-none [&>div]:px-0 [&>div]:py-0 [&>div>div:first-child>h1]:sr-only">
              <MyEventsPage />
            </div>
          )}

          {activeTab === 'menus' && <MenusAdminPanel />}

          {activeTab === 'review' && (
            <div className="[&>div]:max-w-none [&>div]:px-0 [&>div]:py-0 [&>div>h1]:sr-only">
              <ReviewQueuePage />
            </div>
          )}

          {activeTab === 'categories' && (
            <div className="[&>div]:max-w-none [&>div]:px-0 [&>div]:py-0 [&>div>h1]:sr-only">
              <CategoriesPage />
            </div>
          )}

          {activeTab === 'authors' && (
            <div className="[&>div]:max-w-none [&>div]:px-0 [&>div]:py-0 [&>div>h1]:sr-only">
              <AuthorsPage />
            </div>
          )}

          {activeTab === 'earnings' && <CourseOwnerPanel />}
          {activeTab === 'challenges' && (
            <Card className="p-5">
              <h2 className="text-lg font-semibold text-gray-900">Challenges</h2>
              <p className="mt-2 text-sm text-gray-600">Challenge Admin reviews and publishes challenges. The challenge catalog is not wired yet — this is the management home for that role.</p>
            </Card>
          )}
          {activeTab === 'users' && <UsersAdminPage />}
          {activeTab === 'learners' && <LearnersAdminPage />}
          {activeTab === 'reports' && <ReportsAdminPage />}
          {activeTab === 'academy-reports' && <AcademyReportsAdminPage />}
          {activeTab === 'project-reports' && <ProjectReportsAdminPage />}
          {activeTab === 'project-mentor-requests' && <ProjectMentorRequestsAdminPage />}
          {activeTab === 'interview-reports' && <InterviewReportsPanel />}
          {activeTab === 'school-programs' && <SchoolAdminPanel embedded tab="programs" />}
          {activeTab === 'school-interviews' && <SchoolAdminPanel embedded tab="interviews" />}
          {activeTab === 'school-inquiries' && <SchoolAdminPanel embedded tab="inquiries" />}
          {activeTab === 'school-emails' && <SchoolAdminPanel embedded tab="emails" />}
          {activeTab === 'mentor-applications' && <ContributorApplicationsPanel kind="mentors" />}
          {activeTab === 'interviewer-applications' && <ContributorApplicationsPanel kind="interviewers" />}

          {activeTab === 'catalog-settings' && <CatalogSettingsPage />}
          {activeTab === 'legal' && <LegalDocumentsAdminPage />}
          {activeTab === 'llm' && (
            <div className="[&>div]:max-w-none [&>div]:px-0 [&>div]:py-0 [&>div>h1]:sr-only">
              <LlmConfigPage />
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

const MenusAdminPanel: React.FC = () => {
  const [items, setItems] = useState<{ menuKey: string; label: string; path?: string | null; isVisible: boolean; displayOrder: number }[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  useAutoClear(message, setMessage, 5000);

  useEffect(() => {
    contentApi.getNav().then((r) => setItems(r.data.items || [])).catch(() => undefined);
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const response = await contentApi.saveNav(items);
      setItems(response.data.items || items);
      notifyPublicNavChanged();
      setMessage('Menu visibility saved. Hidden items no longer appear in the header.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900">Header menus</h2>
      <p className="mt-2 text-gray-600">Show or hide public header and footer menu items. The same visibility rules apply to Courses, Labs, Projects, Events, Gigs, IDC Academy, Interviews, and Contact. Events and Gigs also require published content before they appear.</p>
      <div className="mt-6 space-y-3">
        {items.filter((item) => item.menuKey !== 'idschool').map((item) => (
          <label key={item.menuKey} className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3">
            <span className="font-medium text-gray-900">
              {item.label}
              <span className="ml-2 text-xs font-normal text-gray-500">({item.menuKey})</span>
            </span>
            <input
              type="checkbox"
              checked={item.isVisible}
              onChange={(e) => setItems((current) => current.map((row) => row.menuKey === item.menuKey ? { ...row, isVisible: e.target.checked } : row))}
            />
          </label>
        ))}
      </div>
      <div className="mt-6">
        <Button type="button" isLoading={saving} onClick={save}>Save menus</Button>
        <ActionStatus message={message} />
      </div>
    </div>
  );
};

export default ContentAdminDashboard;
